import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { toast } from "@/lib/toast-with-sound";
import { useAuth } from "@/contexts/AuthContext";
import { useActionBar } from "@/contexts/ActionBarContext";
import { handleUnauthorizedAccess } from "@/lib/access-control";
import { getTranslatedValue } from "@/components/TranslatableField";
import { usePinnedTripIds, useTrip } from "@/hooks/queries";
import { useExportSettings } from "@/hooks/queries/useSettings";
import { useTogglePin, useToggleLike, useDeleteTrip, useUploadTripLevelPhoto, useDeleteTripLevelPhoto } from "@/hooks/mutations";
import { BACKEND_URL } from "../../config";
const MAX_PHOTOS = 5;

export function useTripDetail() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const { user: authUser, isAuthenticated, loading: authLoading } = useAuth();
  const { setContent: setActionBarContent, clearContent: clearActionBarContent } = useActionBar();

  // React Query hooks
  const { data: pinnedTripIds = new Set() } = usePinnedTripIds();
  const { data: exportSettings } = useExportSettings(isAuthenticated);
  const {
    data: trip,
    isLoading: loading,
    error: tripError,
    refetch: refetchTrip,
  } = useTrip(tripId, { isAuthenticated });
  const togglePinMutation = useTogglePin();
  const toggleLikeMutation = useToggleLike();
  const deleteTripMutation = useDeleteTrip();
  const uploadPhotoMutation = useUploadTripLevelPhoto();
  const deletePhotoMutation = useDeleteTripLevelPhoto();

  // Derived state from React Query
  const isPinned = useMemo(() => pinnedTripIds.has?.(tripId) || pinnedTripIds.includes?.(tripId), [pinnedTripIds, tripId]);
  const tripExportEnabled = exportSettings?.tripExportEnabled ?? true;
  const ownExportEnabled = tripExportEnabled;
  const othersExportEnabled = tripExportEnabled;

  // Local state (UI only)
  const [user, setUser] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [editingCaption, setEditingCaption] = useState(null);
  const [captionText, setCaptionText] = useState("");
  const [draggedPhoto, setDraggedPhoto] = useState(null);
  const [motoLightboxOpen, setMotoLightboxOpen] = useState(false);
  const [currentMotoPhotoIndex, setCurrentMotoPhotoIndex] = useState(0);
  const [alojamentoLightboxOpen, setAlojamentoLightboxOpen] = useState(false);
  const [currentAlojamentoPhotos, setCurrentAlojamentoPhotos] = useState([]);
  const [currentAlojamentoPhotoIndex, setCurrentAlojamentoPhotoIndex] = useState(0);
  const [currentAlojamentoName, setCurrentAlojamentoName] = useState("");
  const [foodLightboxOpen, setFoodLightboxOpen] = useState(false);
  const [currentFoodPhotos, setCurrentFoodPhotos] = useState([]);
  const [currentFoodPhotoIndex, setCurrentFoodPhotoIndex] = useState(0);
  const [currentFoodName, setCurrentFoodName] = useState("");
  const [highlightedPoint, setHighlightedPoint] = useState(null);
  const [activeSegmentTab, setActiveSegmentTab] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Derived state from trip data
  const isLiked = trip?.is_liked || false;
  const likeCount = trip?.like_count || 0;
  const isOwner = trip ? trip.user_id === authUser?.user_id : false;

  // Aggregate photos from all segments and legacy trip-level photos
  const allPhotos = useMemo(() => {
    if (!trip) return [];
    const photos = [];
    if (trip.segments) {
      trip.segments.forEach((seg, segIdx) => {
        if (seg.photos?.length > 0) {
          const sorted = [...seg.photos].sort((a, b) => {
            if (a.is_main && !b.is_main) return -1;
            if (!a.is_main && b.is_main) return 1;
            return (a.order || 0) - (b.order || 0);
          });
          sorted.forEach((photo) => {
            photos.push({
              ...photo,
              segmentId: seg.segment_id,
              segmentIndex: segIdx,
              segmentTitle: getTranslatedValue(seg.title, currentLang),
              is_trip_main: photo.filename === trip.main_photo,
            });
          });
        }
      });
    }
    if (trip.photos?.length > 0) {
      trip.photos.forEach((photo) => {
        photos.push({
          ...photo,
          segmentId: "legacy",
          segmentIndex: null,
          segmentTitle: t("trip_detail.general"),
          is_trip_main: photo.filename === trip.main_photo,
        });
      });
    }
    return photos;
  }, [trip, currentLang, t]);

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (authUser) setUser(authUser);
  }, [authUser]);

  useEffect(() => {
    if (tripError) {
      const status = tripError?.response?.status;
      // 404 (não existe) / 403 (privado) → deixa renderizar o estado
      // not-found com noindex (Soft-404 hardening, 2026-07). NÃO
      // redirecionar para "/" (o Google via como redirect p/ página
      // irrelevante → Soft 404).
      if (status === 404 || status === 403) return;
      toast.error(t("trip_detail.load_error"));
      navigate("/");
    }
  }, [tripError, navigate, t]);

  // ==================== HANDLERS ====================

  const handlePinToggle = () => {
    if (!isAuthenticated) { handleUnauthorizedAccess(navigate); return; }
    togglePinMutation.mutate(
      { tripId, isPinned },
      {
        onSuccess: () => toast.success(isPinned ? t("trip_detail.pin_removed") : t("trip_detail.pin_added")),
        onError: () => toast.error(t("trip_detail.pin_error")),
      }
    );
  };

  const handleLikeToggle = () => {
    if (!isAuthenticated) { handleUnauthorizedAccess(navigate); return; }
    toggleLikeMutation.mutate(
      { tripId, isLiked },
      {
        onSuccess: () => toast.success(isLiked ? t("trip_detail.like_removed") : t("trip_detail.like_added")),
        onError: (error) => {
          if (error.response?.status === 400) {
            toast.error(error.response.data?.detail || t("trip_card.cannot_like"));
          } else {
            toast.error(t("trip_detail.like_error"));
          }
        },
      }
    );
  };

  const handleDelete = () => {
    deleteTripMutation.mutate(tripId, {
      onSuccess: () => { toast.success(t("trip_detail.delete_success")); navigate("/"); },
      onError: () => toast.error(t("trip_detail.delete_error")),
    });
  };

  const handleProtectedDownload = (url, filename, e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      handleUnauthorizedAccess(navigate, { returnTo: `/passeio/${tripId}`, message: t("trip_detail.auth_required.download") });
      window.scrollTo(0, 0);
      return false;
    }
    return true;
  };

  const handleExport = async ({ includePdf = true, includePhotos = false, includeRoutes = true } = {}) => {
    setExporting(true);
    setExportDialogOpen(false);
    try {
      const params = new URLSearchParams({ lang: currentLang });
      if (!includePdf) params.set("include_pdf", "false");
      params.set("include_photos", includePhotos ? "true" : "false");
      if (!includeRoutes) params.set("include_routes", "false");
      const response = await fetch(`/api/trips/${tripId}/export-async?${params}`, { method: "POST", credentials: "include" });
      if (!response.ok) {
        if (response.status === 429) {
          try { const d = await response.json(); toast.error(d.detail || t("trip_detail.export_rate_limit")); } catch { toast.error(t("trip_detail.export_rate_limit")); }
        } else if (response.status === 404) { toast.error(t("trip_detail.export_not_found")); }
        else { toast.error(t("trip_detail.export_error")); }
        return;
      }
      const data = await response.json();
      toast.success(data.message || t("trip_detail.export_processing", "O export está a ser processado. Receberá os links por email."));
    } catch (error) {
      console.error("Export error:", error);
      toast.error(t("trip_detail.export_error"));
    } finally {
      setExporting(false);
    }
  };

  // ==================== PHOTO HANDLERS ====================

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (trip?.photos?.length >= MAX_PHOTOS) { toast.error(t("trip_detail.photo_limit", { count: MAX_PHOTOS })); return; }
    setUploading(true);
    uploadPhotoMutation.mutate(
      { tripId, file },
      {
        onSuccess: () => toast.success(t("trip_detail.photo_uploaded")),
        onError: (error) => toast.error(error.response?.data?.detail || t("trip_detail.photo_upload_error")),
        onSettled: () => setUploading(false),
      }
    );
  };

  const handleDeletePhoto = (filename) => {
    deletePhotoMutation.mutate(
      { tripId, filename },
      {
        onSuccess: () => toast.success(t("trip_detail.photo_deleted")),
        onError: () => toast.error(t("trip_detail.photo_delete_error")),
      }
    );
  };

  const startEditCaption = (photo) => { setEditingCaption(photo.filename); setCaptionText(photo.caption || ""); };

  const saveCaption = async (filename) => {
    try {
      await api.put(`/api/trips/${tripId}/photos/${encodeURIComponent(filename)}/caption`, null, { params: { caption: captionText || null } });
      toast.success(t("trip_detail.caption_saved"));
      setEditingCaption(null);
      refetchTrip();
    } catch { toast.error(t("trip_detail.caption_error")); }
  };

  const setMainPhoto = async (filename) => {
    try {
      await api.put(`/api/trips/${tripId}/photos/${encodeURIComponent(filename)}/main`, null);
      toast.success(t("trip_detail.main_photo_set"));
      refetchTrip();
    } catch { toast.error(t("trip_detail.main_photo_error")); }
  };

  const handlePhotoDragStart = (index) => setDraggedPhoto(index);
  const handlePhotoDragOver = (e, index) => { e.preventDefault(); };
  const handlePhotoDragEnd = async () => {
    if (draggedPhoto === null) return;
    setDraggedPhoto(null);
    try {
      const photoOrder = trip.photos.map((p) => (typeof p === "object" ? p.filename : p));
      await api.put(`/api/trips/${tripId}/photos/reorder`, { photos: photoOrder });
      toast.success(t("trip_detail.photo_order_saved"));
    } catch { toast.error(t("trip_detail.photo_order_error")); refetchTrip(); }
  };

  // ==================== LIGHTBOX HANDLERS ====================

  const openLightbox = (index) => { setCurrentPhotoIndex(index); setLightboxOpen(true); };
  const nextPhoto = () => setCurrentPhotoIndex((prev) => (prev + 1) % (allPhotos.length || 1));
  const prevPhoto = () => setCurrentPhotoIndex((prev) => (prev - 1 + (allPhotos.length || 1)) % (allPhotos.length || 1));

  const openAlojamentoLightbox = (photos, index, name) => { setCurrentAlojamentoPhotos(photos); setCurrentAlojamentoPhotoIndex(index); setCurrentAlojamentoName(name); setAlojamentoLightboxOpen(true); };
  const nextAlojamentoPhoto = () => setCurrentAlojamentoPhotoIndex((prev) => (prev + 1) % (currentAlojamentoPhotos.length || 1));
  const prevAlojamentoPhoto = () => setCurrentAlojamentoPhotoIndex((prev) => (prev - 1 + (currentAlojamentoPhotos.length || 1)) % (currentAlojamentoPhotos.length || 1));

  const openFoodLightbox = (photos, index, name) => { setCurrentFoodPhotos(photos); setCurrentFoodPhotoIndex(index); setCurrentFoodName(name); setFoodLightboxOpen(true); };
  const nextFoodPhoto = () => setCurrentFoodPhotoIndex((prev) => (prev + 1) % (currentFoodPhotos.length || 1));
  const prevFoodPhoto = () => setCurrentFoodPhotoIndex((prev) => (prev - 1 + (currentFoodPhotos.length || 1)) % (currentFoodPhotos.length || 1));

  // ==================== FORMAT HELPERS ====================

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1).toString().padStart(2, "0")}/${date.getFullYear()}`;
  };

  const formatDuration = (hours) => {
    if (!hours) return null;
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (h === 0) return t("trip_detail.duration_format_minutes", { minutes: m });
    if (m === 0) return t("trip_detail.duration_format_hours", { hours: h });
    return t("trip_detail.duration_format_short", { hours: h, minutes: m });
  };

  return {
    // Core
    tripId, navigate, t, i18n, currentLang, authUser, isAuthenticated, authLoading,
    setActionBarContent, clearActionBarContent,
    // Trip data
    trip, loading, isOwner, allPhotos,
    isPinned, isLiked, likeCount,
    ownExportEnabled, othersExportEnabled, tripExportEnabled,
    togglePinMutation, toggleLikeMutation,
    // UI state
    user, uploading, exporting,
    highlightedPoint, setHighlightedPoint,
    activeSegmentTab, setActiveSegmentTab,
    exportDialogOpen, setExportDialogOpen,
    // Photo state
    lightboxOpen, setLightboxOpen, currentPhotoIndex, setCurrentPhotoIndex,
    editingCaption, captionText, setEditingCaption, setCaptionText,
    draggedPhoto,
    // Moto lightbox
    motoLightboxOpen, setMotoLightboxOpen, currentMotoPhotoIndex, setCurrentMotoPhotoIndex,
    // Estada lightbox
    alojamentoLightboxOpen, setAlojamentoLightboxOpen, currentAlojamentoPhotos, currentAlojamentoPhotoIndex, currentAlojamentoName,
    // Food lightbox
    foodLightboxOpen, setFoodLightboxOpen, currentFoodPhotos, currentFoodPhotoIndex, currentFoodName,
    // Handlers
    handlePinToggle, handleLikeToggle, handleDelete,
    handleProtectedDownload, handleExport,
    handlePhotoUpload, handleDeletePhoto,
    startEditCaption, saveCaption, setMainPhoto,
    handlePhotoDragStart, handlePhotoDragOver, handlePhotoDragEnd,
    openLightbox, nextPhoto, prevPhoto,
    openAlojamentoLightbox, nextAlojamentoPhoto, prevAlojamentoPhoto,
    openFoodLightbox, nextFoodPhoto, prevFoodPhoto,
    formatDate, formatDuration,
  };
}
